import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from './AuthContext';
import { applyRefreshedToken, host } from '../Utils';

const AuthState = (props) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [user, setUser] = useState(null);

  // apiFetch dispatches `auth:expired` on any 401 (and clears the token
  // from localStorage). Without this listener, the context still says
  // isAuthenticated=true so PrivateRoute keeps rendering admin pages
  // that then hit 401 in a loop. Subscribe once and force a clean
  // logout + redirect.
  useEffect(() => {
    const onExpired = () => {
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, [navigate]);

  const getAuthToken = () => localStorage.getItem('token');

  const getUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
        const response = await fetch(`${host}/api/auth/getuser`, {
            method: 'POST',
            headers: {
                'authtoken': token
            }
        });
        // 401 → token expired or invalid; force a clean logout.
        if (response.status === 401) {
            localStorage.removeItem('token');
            setIsAuthenticated(false);
            setUser(null);
            return;
        }
        applyRefreshedToken(response);
        if (!response.ok) {
            console.error("getUser failed:", response.status, response.statusText);
            return;
        }
        const json = await response.json();
        // Server convention: { success, data }. Auth path uses raw fetch
        // (not apiFetch) so we unwrap here.
        setUser(json.data ?? json);
    } catch (error) {
        console.error("Error fetching user:", error);
    }
  }, []);

  const updateUser = async (userData) => {
    const token = getAuthToken();
    try {
        const response = await fetch(`${host}/api/auth/updateuser`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'authtoken': token
            },
            body: JSON.stringify(userData)
        });

        applyRefreshedToken(response);

        if (!response.ok) {
            const text = await response.text();
            console.error("Server error:", text);
            return { success: false, error: `Server Error: ${response.status} ${response.statusText}` };
        }

        const json = await response.json();
        if (json.success) {
            setUser(json.data ?? json.user);
            // Notify any listeners (Footer, ContactUs) that the public
            // profile may have changed so they can re-fetch immediately.
            window.dispatchEvent(new CustomEvent('public-profile-updated'));
            return { success: true };
        } else {
            return { success: false, error: json.error };
        }
    } catch (error) {
        console.error("Error updating user:", error);
        return { success: false, error: "Network or Server Error" };
    }
  };

  // Surface non-2xx responses to the caller with the most specific
  // error message available — validator messages > top-level error
  // string > status text. Previous logic only intercepted 5xx, so a
  // 400 with non-JSON body fell through to .json() and threw,
  // ending up as a generic "Network error" — masking the real cause.
  const surfaceError = async (response, fallback) => {
    let errMsg = `Server error (${response.status})`;
    try {
        const body = await response.json();
        errMsg = (body.errors && body.errors[0]?.msg) || body.error || errMsg;
    } catch { /* non-JSON body — keep status-based fallback */ }
    return { success: false, error: errMsg || fallback };
  };

  const Login = async (email, password) => {
    try {
        const response = await fetch(`${host}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });
        if (!response.ok) return surfaceError(response, 'Invalid credentials');

        const json = await response.json();
        if (json.success) {
            const authtoken = json.data?.authtoken ?? json.authtoken;
            localStorage.setItem('token', authtoken);
            setIsAuthenticated(true);
            await getUser();
            navigate('/');
            return { success: true };
        }
        return { success: false, error: json.error || 'Invalid credentials' };
    } catch (error) {
        console.error('Login error:', error);
        return { success: false, error: 'Network error' };
    }
  };

  const Signup = async (credentials) => {
    const { name, email, password, confirmpassword, key } = credentials;
    try {
        const response = await fetch(`${host}/api/auth/createuser`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email, password, confirmpassword, key }),
        });
        if (!response.ok) return surfaceError(response, 'Invalid details');

        const json = await response.json();
        if (json.success) {
            const authtoken = json.data?.authtoken ?? json.authtoken;
            localStorage.setItem('token', authtoken);
            setIsAuthenticated(true);
            await getUser();
            navigate('/');
            return { success: true };
        }
        return { success: false, error: json.error || 'Invalid details' };
    } catch (error) {
        console.error('Signup error:', error);
        return { success: false, error: 'Network error' };
    }
  };

  const ChangePassword = async ({ currentPassword, newPassword }) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${host}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authtoken: token,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      applyRefreshedToken(response);
      if (!response.ok && response.status >= 500) {
        return { success: false, error: `Server error (${response.status})` };
      }
      const json = await response.json();
      if (json.success) return { success: true };
      return { success: false, error: json.error || (json.errors && json.errors[0]?.msg) || 'Failed' };
    } catch (error) {
      console.error('ChangePassword error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const ResetPasswordWithKey = async ({ email, key, newPassword }) => {
    try {
      const response = await fetch(`${host}/api/auth/reset-password-with-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, key, newPassword }),
      });
      if (!response.ok && response.status >= 500) {
        return { success: false, error: `Server error (${response.status})` };
      }
      const json = await response.json();
      if (json.success) return { success: true };
      return { success: false, error: json.error || (json.errors && json.errors[0]?.msg) || 'Failed' };
    } catch (error) {
      console.error('ResetPassword error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const Logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, Login, Signup, Logout, getUser, updateUser, ChangePassword, ResetPasswordWithKey }}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthState;
